// src/components/projects/ProjectList.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProjects } from '../../hooks/useProjects';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import Pagination from '../common/Pagination';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import {
    PlusIcon, PencilSquareIcon, TrashIcon,
    MagnifyingGlassIcon, MapPinIcon, CalendarIcon,
} from '@heroicons/react/24/outline';

const STATUSES = [
    { name: '', label: 'Tutti' },
    { name: 'DRAFT', label: 'Bozza' },
    { name: 'PLANNED', label: 'Pianificato' },
    { name: 'IN_PROGRESS', label: 'In Corso' },
    { name: 'ON_HOLD', label: 'In Attesa' },
    { name: 'COMPLETED', label: 'Completato' },
];

const PRIORITIES = [
    { name: 'LOW', label: 'Bassa', dot: 'bg-gray-400' },
    { name: 'MEDIUM', label: 'Media', dot: 'bg-blue-400' },
    { name: 'HIGH', label: 'Alta', dot: 'bg-orange-400' },
    { name: 'URGENT', label: 'Urgente', dot: 'bg-red-500' },
    { name: 'CRITICAL', label: 'Critica', dot: 'bg-red-700' },
];

const PRIORITY_DOT = {
    LOW: 'bg-gray-400', MEDIUM: 'bg-blue-400',
    HIGH: 'bg-orange-400', URGENT: 'bg-red-500', CRITICAL: 'bg-red-700',
};

const DEFAULT_FILTERS = { status: '', priority: '', search: '', page: 1, limit: 10 };

const ProjectList = () => {
    const { t } = useTranslation();
    const { organization } = useAuth();
    const currency = organization?.currency || 'EUR';

    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

    const { projects, loading, error, pagination, deleteProject } = useProjects(filters);

    const setFilter = (key, value) =>
        setFilters(prev => ({ ...prev, [key]: value, page: 1 }));

    const handleDelete = async (id) => {
        await deleteProject(id);
        setShowDeleteConfirm(null);
    };

    if (loading && projects.length === 0) return <LoadingSpinner />;

    return (
        <div className="space-y-6">

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">{t('projects.title')}</h1>
                    <p className="text-gray-500 text-sm mt-0.5">{pagination.total || projects.length} progetti totali</p>
                </div>
                <Link to="/projects/new"
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-xl hover:bg-blue-700 transition font-medium text-sm">
                    <PlusIcon className="w-5 h-5" /> Nuovo Progetto
                </Link>
            </div>

            {/* Filtri */}
            <div className="bg-white rounded-2xl border p-5 space-y-4">
                <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" value={filters.search}
                        onChange={e => setFilter('search', e.target.value)}
                        placeholder="Cerca per nome o codice..."
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                </div>

                <div className="flex flex-wrap gap-2">
                    {STATUSES.map(s => (
                        <button key={s.name} onClick={() => setFilter('status', s.name)}
                            className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${filters.status === s.name
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}>
                            {s.label}
                        </button>
                    ))}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Priorità:</span>
                    {PRIORITIES.map(p => (
                        <button key={p.name}
                            onClick={() => setFilter('priority', filters.priority === p.name ? '' : p.name)}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${filters.priority === p.name
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${filters.priority === p.name ? 'bg-white' : p.dot}`} />
                            {p.label}
                        </button>
                    ))}
                    {(filters.status || filters.priority || filters.search) && (
                        <button onClick={() => setFilters(DEFAULT_FILTERS)}
                            className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-500 hover:bg-red-100 transition">
                            Reset
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">⚠️ {error}</div>
            )}

            {/* Lista */}
            {projects.length === 0 && !loading ? (
                <div className="bg-white rounded-2xl border p-16 text-center">
                    <div className="text-5xl mb-4">🏗️</div>
                    <h3 className="text-lg font-semibold text-gray-900">Nessun progetto trovato</h3>
                    <p className="text-gray-500 mt-1 mb-6 text-sm">
                        {filters.status || filters.priority || filters.search
                            ? 'Prova a modificare i filtri'
                            : 'Crea il tuo primo progetto per iniziare'
                        }
                    </p>
                    <Link to="/projects/new"
                        className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-xl hover:bg-blue-700 transition font-medium">
                        <PlusIcon className="w-5 h-5" /> Crea Progetto
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {projects.filter(Boolean).map((project) => {
                        const utilization = project.estimatedBudget > 0
                            ? Math.min(100, ((project.actualCost || 0) / project.estimatedBudget) * 100)
                            : 0;
                        const budgetColor = utilization >= 90 ? 'bg-red-500'
                            : utilization >= 70 ? 'bg-yellow-400' : 'bg-emerald-500';

                        return (
                            <div key={project.id}
                                className="bg-white rounded-2xl border hover:border-blue-200 hover:shadow-md transition-all group">
                                <div className="p-6">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                <StatusBadge status={project.status} />
                                                <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md">
                                                    {project.code}
                                                </span>
                                                {project.priority && (
                                                    <span className="flex items-center gap-1 text-xs">
                                                        <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[project.priority]}`} />
                                                        <span className="text-gray-400">
                                                            {PRIORITIES.find(p => p.name === project.priority)?.label}
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                            <Link to={`/projects/${project.id}`}>
                                                <h3 className="text-base font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                                                    {project.name}
                                                </h3>
                                            </Link>
                                            {project.description && (
                                                <p className="text-gray-400 text-sm mt-1 line-clamp-2">{project.description}</p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Link to={`/projects/${project.id}/edit`}
                                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition">
                                                <PencilSquareIcon className="w-4 h-4" />
                                            </Link>
                                            <button onClick={() => setShowDeleteConfirm(project.id)}
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-400">
                                        {project.location && (
                                            <span className="flex items-center gap-1">
                                                <MapPinIcon className="w-3.5 h-3.5" />{project.location}
                                            </span>
                                        )}
                                        <span className="flex items-center gap-1">
                                            <CalendarIcon className="w-3.5 h-3.5" />{formatDate(project.startDate)}
                                        </span>
                                        {project._count && (
                                            <span>📋 {project._count.tasks || 0} task · 💰 {project._count.expenses || 0} spese</span>
                                        )}
                                    </div>

                                    <div className="mt-4 grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs text-gray-400 mb-1">Budget</p>
                                            <p className="text-sm font-semibold text-gray-900">
                                                {formatCurrency(project.estimatedBudget, currency)}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-400 mb-1">Progresso</p>
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                    <div className="h-full bg-blue-500 rounded-full"
                                                        style={{ width: `${project.progress || 0}%` }} />
                                                </div>
                                                <span className="text-xs font-semibold text-gray-700">{project.progress || 0}%</span>
                                            </div>
                                        </div>
                                    </div>

                                    {project.actualCost > 0 && (
                                        <div className="mt-3">
                                            <div className="flex justify-between text-xs text-gray-400 mb-1">
                                                <span>Budget utilizzato</span>
                                                <span className={utilization >= 90 ? 'text-red-500 font-semibold' : ''}>
                                                    {utilization.toFixed(1)}%
                                                </span>
                                            </div>
                                            <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                                <div className={`h-full ${budgetColor} rounded-full transition-all`}
                                                    style={{ width: `${utilization}%` }} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {pagination.totalPages > 1 && (
                <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    onPageChange={page => setFilters(prev => ({ ...prev, page }))}
                />
            )}

            {showDeleteConfirm && (
                <ConfirmDialog
                    title="Elimina Progetto"
                    message="Sei sicuro? Verranno eliminate anche tutte le spese e task associate."
                    confirmText="Elimina"
                    type="danger"
                    onConfirm={() => handleDelete(showDeleteConfirm)}
                    onCancel={() => setShowDeleteConfirm(null)}
                />
            )}
        </div>
    );
};

export default ProjectList;
